import * as DialogPrimitive from '@radix-ui/react-dialog'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown, User } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from 'next-themes'
import evtimiaLogoBlue from '@/assets/evtimia-albastru.png'
import evtimiaLogoWhite from '@/assets/evtimia-alb.png'
import { ThemeToggle } from './ThemeToggle'
import { LanguageSwitcher } from './LanguageSwitcher'
import { ServicesDropdown } from './ServicesDropdown'
import { getServices } from '@/data/servicesData'
import { useTranslation } from '@/i18n'
import { useAuth } from '@/context/AuthContext'

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  const { resolvedTheme } = useTheme()
  const { t, language } = useTranslation()
  const { loading, session, isStaff } = useAuth()
  // A session alone is not enough to pick a surface. `roles` starts empty and
  // is filled a tick later (AuthContext defers the profile read), so between
  // the two `isStaff` reads false and a signed-in admin would be offered the
  // client portal before the link corrected itself to the CRM. Waiting for
  // `loading` means one change instead of two: an unresolved account is shown
  // the signed-out buttons, which is also the honest answer while it lasts.
  const accountKnown = !loading && !!session
  const dashboardTo = isStaff ? '/crm' : '/portal'
  const dashboardLabel = isStaff ? t.nav.crmDashboard : t.nav.clientPortal

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Services is rendered separately as a dropdown, so it is not in this list.
  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/portfolio', label: t.nav.work },
    { to: '/about', label: t.nav.about },
    { to: '/contact', label: t.nav.contact },
  ]

  const services = getServices(language)

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setIsMobileServicesOpen(false)
  }

  return (
    <>
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 w-full z-[110]"
      >
        <div className={`w-full px-6 sm:px-8 lg:px-12 py-4 transition-all duration-300 ${
          isScrolled ? 'glass-navbar' : 'bg-transparent'
        }`}>
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="cursor-pointer hover-scale hover-scale-sm">
              <Link to="/">
                <img src={mounted && resolvedTheme === 'dark' ? evtimiaLogoWhite : evtimiaLogoBlue} alt="EVTIMIA" width={1173} height={360} decoding="async" className="h-8 sm:h-10 w-auto" />
              </Link>
            </div>

            <div className="hidden lg:flex items-center gap-6">
              <Link
                to="/"
                className="text-foreground/80 hover:text-primary-text font-medium gentle-animation text-sm"
              >
                {t.nav.home}
              </Link>

              <ServicesDropdown />

              {navLinks.slice(1).map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-foreground/80 hover:text-primary-text font-medium gentle-animation text-sm"
                >
                  {link.label}
                </Link>
              ))}
              <ThemeToggle />
              <LanguageSwitcher />
              <div className="flex items-center gap-2">
                {accountKnown ? (
                  <Link
                    to={dashboardTo}
                    className="hover-scale bg-primary text-primary-foreground font-semibold px-3 py-2 rounded-lg hover:opacity-90 gentle-animation cursor-pointer text-sm flex items-center gap-1.5"
                  >
                    <User className="w-4 h-4" />
                    {dashboardLabel}
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login?mode=signup"
                      className="hover-scale text-primary-text font-semibold px-3 py-2 rounded-lg hover:bg-primary/5 gentle-animation cursor-pointer text-sm"
                    >
                      {t.nav.signUp}
                    </Link>
                    <Link
                      to="/login"
                      className="hover-scale bg-primary text-primary-foreground font-semibold px-3 py-2 rounded-lg hover:opacity-90 gentle-animation cursor-pointer text-sm"
                    >
                      {t.nav.login}
                    </Link>
                  </>
                )}
              </div>
            </div>

            <div className="lg:hidden flex items-center gap-1">
              <ThemeToggle />
              <LanguageSwitcher />
              {/* Open only. The drawer is a modal dialog, so while it is open
                  Radix sets pointer-events: none on <body> and this button —
                  which lives outside the portal — is inert. It used to render
                  an X there, so the one control a visitor reached for to close
                  the menu silently did nothing. The X now lives inside the
                  drawer, where it can actually be clicked. */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="w-11 h-11 flex items-center justify-center rounded-lg text-foreground hover:bg-primary/5 gentle-animation cursor-pointer"
                aria-label={t.a11y.openMenu}
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* This is the only navigation below lg, and it was a bare motion.div — no
          role, no focus trap, no Escape, no focus restore — so opening it left
          focus on the hamburger and a keyboard user tabbed straight into the page
          behind an opaque scrim. The two authenticated shells were migrated to
          Radix; the public surface, which most visitors actually use, was not.

          Radix Dialog supplies the semantics, focus trap, focus restore, Escape
          and scroll lock; `asChild` + `forceMount` keep the framer-motion spring
          so the drawer still feels the way it did. */}
      <DialogPrimitive.Root
        open={isMobileMenuOpen}
        onOpenChange={open => {
          if (!open) closeMobileMenu()
        }}
      >
        <AnimatePresence>
          {isMobileMenuOpen && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  // Above the header (z-110), not below it: the header is inert
                  // while the drawer is open, so leaving it undimmed on top of
                  // the scrim advertised controls that could not be used.
                  className="lg:hidden fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-[115]"
                />
              </DialogPrimitive.Overlay>
              <DialogPrimitive.Content asChild forceMount aria-describedby={undefined}>
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: '0%' }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="lg:hidden fixed top-0 right-0 h-full w-72 max-w-[85vw] bg-background border-l border-border z-[120] shadow-xl overflow-y-auto"
      >
        <DialogPrimitive.Title className="sr-only">{t.nav.home}</DialogPrimitive.Title>
        <div className="h-16 flex items-center justify-end px-4 border-b border-border">
          <DialogPrimitive.Close asChild>
            <button
              aria-label={t.a11y.closeMenu}
              className="w-11 h-11 flex items-center justify-center rounded-lg text-foreground hover:bg-primary/5 gentle-animation cursor-pointer"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </DialogPrimitive.Close>
        </div>
        <div className="flex flex-col pt-4 pb-8 px-6 gap-2">
          <Link
            to="/"
            className="px-4 py-3 text-foreground hover:text-primary-text hover:bg-primary/5 rounded-lg gentle-animation font-medium text-lg"
            onClick={closeMobileMenu}
          >
            {t.nav.home}
          </Link>

          {/* "Services" opens the Services page, as on desktop; the chevron
              beside it shows the list. That replaced an "All services" item at
              the end of the list (14 October 2026). */}
          <div className="flex items-center rounded-lg gentle-animation hover:bg-primary/5">
            <Link
              to="/services"
              className="flex-1 px-4 py-3 text-foreground hover:text-primary-text gentle-animation font-medium text-lg"
              onClick={closeMobileMenu}
            >
              {t.nav.services}
            </Link>
            <button
              type="button"
              onClick={() => setIsMobileServicesOpen(v => !v)}
              aria-expanded={isMobileServicesOpen}
              aria-label={t.nav.services}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-foreground hover:text-primary-text gentle-animation cursor-pointer"
            >
              <ChevronDown
                className={`w-4 h-4 gentle-animation ${isMobileServicesOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
          </div>

          {isMobileServicesOpen && (
            <div className="flex flex-col gap-1 pl-3 border-l border-border ml-4 mb-1">
              {services.map(service => (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="px-4 py-2 text-muted-foreground hover:text-primary-text hover:bg-primary/5 rounded-lg gentle-animation text-sm"
                  onClick={closeMobileMenu}
                >
                  {service.title}
                </Link>
              ))}
            </div>
          )}

          {navLinks.slice(1).map(link => (
            <Link
              key={link.to}
              to={link.to}
              className="px-4 py-3 text-foreground hover:text-primary-text hover:bg-primary/5 rounded-lg gentle-animation font-medium text-lg"
              onClick={closeMobileMenu}
            >
              {link.label}
            </Link>
          ))}

          {accountKnown ? (
            <Link
              to={dashboardTo}
              onClick={closeMobileMenu}
              className="flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg hover:opacity-90 gentle-animation mt-4 cursor-pointer text-center"
            >
              <User className="w-4 h-4" />
              {dashboardLabel}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                onClick={closeMobileMenu}
                className="bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg hover:opacity-90 gentle-animation mt-4 cursor-pointer text-center"
              >
                {t.nav.login}
              </Link>

              {/* Same pair as the desktop header: this one is the sign-up
                  half, so it carries ?mode=signup rather than dropping the
                  visitor on the sign-in form with no hint they can register. */}
              <Link
                to="/login?mode=signup"
                onClick={closeMobileMenu}
                className="flex items-center justify-center gap-2 px-4 py-3 text-foreground hover:text-primary hover:bg-primary/5 rounded-lg gentle-animation font-medium border border-border"
              >
                <User className="w-4 h-4" />
                {t.nav.signUp}
              </Link>
            </>
          )}
        </div>
      </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>
    </>
  )
}
